/**
 * Multi-ccTLD domain configuration for Relark.
 *
 * Each market has its own ccTLD that becomes the canonical site for that
 * locale. relark.app serves international (en/es/fr/pt) via path prefixes.
 * relark.eu hosts an EU-compliance landing (Trust Center).
 *
 * This file is the single source of truth — all SEO hooks, sitemap
 * generation, llms.txt generation, language switching, and JSON-LD
 * derive their origins from here.
 */

import type { SeoLang } from "@/lib/seo-config";

export type DomainRole = "ccTLD" | "international" | "trust" | "dev";

export interface DomainConfig {
  hostname: string;
  origin: string;
  defaultLang: SeoLang;
  role: DomainRole;
}

/**
 * Per-language canonical origin. Used for canonical URLs, og:url,
 * hreflang alternates, JSON-LD @id, and sitemap loc entries.
 *
 * Nordic + DACH ccTLDs map 1:1 to their local language. Everything else
 * lives on relark.app under a path prefix (e.g. /es, /fr, /pt).
 */
export const ORIGIN_BY_LANG: Record<SeoLang, string> = {
  da: "https://relark.dk",
  nb: "https://relark.app",
  sv: "https://relark.se",
  de: "https://relark.de",
  en: "https://relark.app",
  es: "https://relark.app",
  fr: "https://relark.app",
  pt: "https://relark.app",
};

/**
 * Per-language URL path. ccTLD languages always live at "/" because the
 * ccTLD itself encodes the locale. relark.app uses "/" for English and
 * path prefixes for ES/FR/PT.
 */
export const PATH_BY_LANG: Record<SeoLang, string> = {
  da: "/",
  nb: "/no",
  sv: "/",
  de: "/",
  en: "/",
  es: "/es",
  fr: "/fr",
  pt: "/pt",
};

/**
 * hostname → DomainConfig lookup. Covers both apex and `www.` variants.
 * Unknown hostnames (lovable.app preview, localhost, custom dev) fall
 * back to the dev config which renders the Danish landing page.
 */
const HOSTNAME_MAP: Record<string, DomainConfig> = {
  "relark.dk":      { hostname: "relark.dk",      origin: "https://relark.dk", defaultLang: "da", role: "ccTLD" },
  "www.relark.dk":  { hostname: "relark.dk",      origin: "https://relark.dk", defaultLang: "da", role: "ccTLD" },
  "relark.se":      { hostname: "relark.se",      origin: "https://relark.se", defaultLang: "sv", role: "ccTLD" },
  "www.relark.se":  { hostname: "relark.se",      origin: "https://relark.se", defaultLang: "sv", role: "ccTLD" },
  "relark.de":      { hostname: "relark.de",      origin: "https://relark.de", defaultLang: "de", role: "ccTLD" },
  "www.relark.de":  { hostname: "relark.de",      origin: "https://relark.de", defaultLang: "de", role: "ccTLD" },
  "relark.app":     { hostname: "relark.app",     origin: "https://relark.app", defaultLang: "en", role: "international" },
  "www.relark.app": { hostname: "relark.app",     origin: "https://relark.app", defaultLang: "en", role: "international" },
  "relark.eu":      { hostname: "relark.eu",      origin: "https://relark.eu", defaultLang: "en", role: "trust" },
  "www.relark.eu":  { hostname: "relark.eu",      origin: "https://relark.eu", defaultLang: "en", role: "trust" },
};

const DEV_CONFIG: DomainConfig = {
  hostname: "dev",
  origin: "https://relark.dk", // Use the canonical origin so generated SEO matches production
  defaultLang: "da",
  role: "dev",
};

/**
 * Resolve the active domain config from a hostname (or window.location).
 * Always returns a valid config — falls back to the dev config for any
 * unknown host (preview links, localhost, custom dev domains).
 */
export function getDomainConfig(hostname?: string): DomainConfig {
  const host =
    hostname ??
    (typeof window !== "undefined" ? window.location.hostname : "");
  if (!host) return DEV_CONFIG;
  return HOSTNAME_MAP[host.toLowerCase()] ?? DEV_CONFIG;
}

/**
 * Origin for a given language — used to build canonical URLs, og:url,
 * hreflang alternates and JSON-LD @id values that always point to the
 * correct ccTLD.
 */
export function originFor(lang: SeoLang): string {
  return ORIGIN_BY_LANG[lang];
}

/**
 * Build the canonical URL for a given language.
 * Always strips trailing slash on non-root paths to avoid duplicate-content
 * canonicalisation issues.
 */
export function urlFor(lang: SeoLang, path?: string): string {
  const origin = originFor(lang);
  const finalPath = path ?? PATH_BY_LANG[lang];
  if (finalPath === "/" || finalPath === "") return `${origin}/`;
  return `${origin}${finalPath.startsWith("/") ? finalPath : `/${finalPath}`}`;
}

/**
 * All ccTLD origins (used by sitemap-index generation, robots.txt etc).
 */
export const ALL_ORIGINS: string[] = Array.from(
  new Set(Object.values(ORIGIN_BY_LANG)),
);

/**
 * Friendly per-domain display label for footers / language switcher.
 */
export const DOMAIN_LABELS: Record<string, string> = {
  "relark.dk": "relark.dk",
  "relark.se": "relark.se",
  "relark.de": "relark.de",
  "relark.app": "relark.app",
  "relark.eu": "relark.eu",
};